import React from 'react';
import { 
  ClockIcon, 
  CurrencyEuroIcon, 
  ExclamationTriangleIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';

interface PathOption {
  title: string;
  subtitle: string;
  timeline: string;
  budget: number;
  riskLevel: 'Alto' | 'Medio' | 'Basso';
  successRate: string;
  features: string[];
  drawbacks: string[];
  variant: 'diy' | 'partnership';
}

interface PathComparisonProps {
  originalBudget: number;
  selectedAgent?: string;
  agentComplexity?: string;
  onSelectPath: (path: 'diy' | 'partnership') => void;
}

const PathComparison: React.FC<PathComparisonProps> = ({ originalBudget, selectedAgent, agentComplexity, onSelectPath }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Determine _blank pricing based on agent type and complexity
  const getBlankPricing = () => {
    const agentName = selectedAgent?.toLowerCase() || '';
    const complexity = agentComplexity?.toLowerCase() || 'medium';

    // Base pricing by agent type
    let baseRange = { min: 15000, max: 35000 }; // Default range

    if (agentName.includes('customer service') || agentName.includes('chatbot')) {
      // Customer Service Bot: Medium complexity, standard integration
      baseRange = { min: 18000, max: 32000 };
    } else if (agentName.includes('product recommendation') || agentName.includes('recommendation engine')) {
      // Product Recommendation: High complexity, ML models
      baseRange = { min: 28000, max: 45000 };
    } else if (agentName.includes('inventory') || agentName.includes('stock')) {
      // Inventory Optimization: Lower complexity, data analysis
      baseRange = { min: 12000, max: 22000 };
    } else if (agentName.includes('marketing') || agentName.includes('automation')) {
      // Marketing Automation: Medium-High complexity
      baseRange = { min: 22000, max: 38000 };
    } else if (agentName.includes('personal shopping') || agentName.includes('shopping assistant')) {
      // Personal Shopping Assistant: High complexity, personalization
      baseRange = { min: 25000, max: 42000 };
    }

    // Adjust for complexity
    const complexityMultiplier = {
      'low': 0.8,
      'medium': 1.0,
      'high': 1.3
    }[complexity] || 1.0;

    const adjustedMin = Math.round(baseRange.min * complexityMultiplier);
    const adjustedMax = Math.round(baseRange.max * complexityMultiplier);

    // If user budget is within range, use user budget as max
    // If user budget is below range, show range but note budget constraints
    if (originalBudget >= adjustedMin && originalBudget <= adjustedMax) {
      return { min: adjustedMin, max: originalBudget, withinBudget: true };
    } else if (originalBudget > adjustedMax) {
      return { min: adjustedMin, max: adjustedMax, withinBudget: true };
    } else {
      return { min: adjustedMin, max: adjustedMax, withinBudget: false };
    }
  };

  const blankPricing = getBlankPricing();

  const diyOption: PathOption = {
    title: "Percorso Fai-da-Te",
    subtitle: "Implementazione interna",
    timeline: "12-18 mesi",
    budget: Math.round(originalBudget * 1.8), // Costi nascosti e inefficienze
    riskLevel: "Alto",
    successRate: "30%",
    features: [
      "Team interno dedicato",
      "Controllo completo processo", 
      "Apprendimento organizzativo",
      "Nessun costo consulenza"
    ],
    drawbacks: [
      "Necessita hiring specialisti AI (€80k+ per developer)",
      "Curva apprendimento 6-12 mesi",
      "Alto rischio di fallimento (70%)",
      "Costi nascosti: formazione, errori, ritardi",
      "Nessuna garanzia di risultati",
      "Supporto post-lancio tutto interno"
    ],
    variant: "diy"
  };

  const partnershipOption: PathOption = {
    title: "Partnership _blank Growth Agency",
    subtitle: "Accelera il tuo successo AI",
    timeline: "120-180 giorni",
    budget: blankPricing.max, // Use _blank's pricing
    riskLevel: "Basso", 
    successRate: "95%",
    features: [
      "Team di specialisti AI dedicati",
      "6 mesi supporto post-lancio incluso",
      "Training completo team interno"
    ],
    drawbacks: [
      "Costo iniziale più alto", 
      "Dipendenza da partner esterno (iniziale)"
    ],
    variant: "partnership"
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'Alto': return 'text-red-600 bg-red-50';
      case 'Medio': return 'text-yellow-600 bg-yellow-50';
      case 'Basso': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const PathCard: React.FC<{ option: PathOption; featured?: boolean }> = ({ option, featured = false }) => (
    <div className={`relative bg-white rounded-xl shadow-lg p-8 ${featured ? 'ring-2 ring-primary-500 scale-105' : 'border border-gray-200'}`}>
      {featured && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary-500 text-white px-6 py-2 rounded-full text-sm font-medium flex items-center">
            <StarIcon className="h-4 w-4 mr-1" />
            CONSIGLIATO
          </div>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{option.title}</h3>
        <p className="text-gray-600">{option.subtitle}</p>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="flex items-center">
          <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm text-gray-500">Timeline</div>
            <div className="font-semibold">{option.timeline}</div>
          </div>
        </div>
        
        <div className="flex items-center">
          <CurrencyEuroIcon className="h-5 w-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm text-gray-500">Investimento</div>
            {option.variant === 'partnership' ? (
              <div>
                <div className="font-semibold">
                  {formatCurrency(blankPricing.min)} - {formatCurrency(blankPricing.max)}
                </div>
                {!blankPricing.withinBudget && (
                  <div className="text-xs text-amber-600">Budget da rivalutare</div>
                )}
              </div>
            ) : (
              <div className="font-semibold">{formatCurrency(option.budget)}</div>
            )}
          </div>
        </div>
        
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm text-gray-500">Rischio</div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(option.riskLevel)}`}>
              {option.riskLevel}
            </div>
          </div>
        </div>
        
        <div className="flex items-center">
          <ChartBarIcon className="h-5 w-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm text-gray-500">Success Rate</div>
            <div className="font-semibold">{option.successRate}</div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="mb-6">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
          Vantaggi
        </h4>
        <ul className="space-y-2">
          {option.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Drawbacks */}
      <div className="mb-8">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
          Considerazioni
        </h4>
        <ul className="space-y-2">
          {option.drawbacks.map((drawback, index) => (
            <li key={index} className="flex items-start">
              <XCircleIcon className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">{drawback}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA Button */}
      <button
        onClick={() => onSelectPath(option.variant)}
        className={`w-full py-3 px-4 rounded-lg font-medium text-center transition-colors ${
          featured
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        {option.variant === 'partnership' ? 'Prenota Call Gratuita' : 'Procedi In Autonomia'}
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Scegli il Tuo Percorso di Implementazione AI
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Hai due opzioni per implementare la tua soluzione AI. Ogni percorso ha i suoi pro e contro. 
            La scelta dipende dalle tue risorse, timeline e tolleranza al rischio.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <PathCard option={diyOption} />
          <PathCard option={partnershipOption} featured={true} />
        </div>

        {/* ROI Comparison */}
        <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Comparazione ROI & Time-to-Value
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="text-red-600 text-4xl font-bold mb-2">30%</div>
              <div className="text-sm text-gray-600 mb-1">Success Rate Fai-da-Te</div>
              <div className="text-xs text-gray-500">Tempo medio per vedere risultati: 18+ mesi</div>
            </div>
            
            <div className="text-center">
              <div className="text-green-600 text-4xl font-bold mb-2">95%</div>
              <div className="text-sm text-gray-600 mb-1">Success Rate con _blank</div>
              <div className="text-xs text-gray-500">ROI medio: 300% in 6 mesi</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathComparison;